import React, { useEffect, useMemo, Suspense } from 'react';
import api from '@/lib/api';
import SaaSLandingPage from '@/pages/SaaSLandingPage';
import HeroMockPage from '@/pages/HeroMockPage';
import SecurityNoticeBanner from '@/components/SecurityNoticeBanner';

/**
 * Hero A/B Test router.
 *
 * Three variants split equally on first visit, then sticky for 30 days
 * via localStorage. Impressions & CTA clicks pulse to the backend so the
 * Founder Cockpit can see which variant converts best over time.
 *
 *   A = Live (current Sky-Blue SaaSLandingPage)
 *   B = Mock v2, MALE   surgeon (dark hero)
 *   C = Mock v2, FEMALE surgeon (dark hero)
 *
 * Override for QA: append ?ab=A | ?ab=B | ?ab=C to /  (also persists).
 */

const STORAGE_KEY = 'docsite_hero_variant';
const STORAGE_TS_KEY = 'docsite_hero_variant_ts';
const STICKY_DAYS = 30;
const VARIANTS = ['A', 'B', 'C'];

function pickVariant() {
  // Allow URL override for QA / specific links
  try {
    const url = new URL(window.location.href);
    const forced = (url.searchParams.get('ab') || '').toUpperCase();
    if (VARIANTS.includes(forced)) {
      localStorage.setItem(STORAGE_KEY, forced);
      localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
      return forced;
    }
  } catch (_) { /* SSR safe */ }

  // Sticky bucket
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    const ts = parseInt(localStorage.getItem(STORAGE_TS_KEY) || '0', 10);
    const fresh = ts && (Date.now() - ts) < STICKY_DAYS * 86400_000;
    if (fresh && VARIANTS.includes(existing)) return existing;
  } catch (_) { /* */ }

  // Fresh assignment — equal-probability random
  const v = VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  try {
    localStorage.setItem(STORAGE_KEY, v);
    localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
  } catch (_) { /* */ }
  return v;
}

const CTA_PATTERNS = [
  'get-started', 'cta-', 'register', 'brochure', 'demo', 'turbo-cta',
  'hero-cta', 'lifecycle-slideover-cta', 'mock-book-btn', 'mock-cta-header',
  'mock-login-btn',
];

export default function HomeABTest() {
  const variant = useMemo(() => pickVariant(), []);

  // Fire impression once per session per variant
  useEffect(() => {
    const sessKey = `docsite_hero_impr_${variant}`;
    if (sessionStorage.getItem(sessKey)) return;
    sessionStorage.setItem(sessKey, '1');
    api.post('/track/hero-variant', {
      variant,
      event: 'impression',
      page_path: '/',
    }).catch(() => { /* silent */ });
  }, [variant]);

  // Capture CTA clicks anywhere within the hero/page
  useEffect(() => {
    const handler = (e) => {
      try {
        const el = e.target.closest('[data-testid]');
        if (!el) return;
        const tid = (el.getAttribute('data-testid') || '').toLowerCase();
        if (!CTA_PATTERNS.some(p => tid.includes(p))) return;
        api.post('/track/hero-variant', {
          variant,
          event: 'cta_click',
          cta: tid.slice(0, 120),
          page_path: window.location.pathname || '/',
        }).catch(() => { /* silent */ });
      } catch (_) { /* never block UX */ }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [variant]);

  if (variant === 'B') {
    return (
      <Suspense fallback={null}>
        <SecurityNoticeBanner />
        <HeroMockPage lockedVariant="male" />
      </Suspense>
    );
  }
  if (variant === 'C') {
    return (
      <Suspense fallback={null}>
        <SecurityNoticeBanner />
        <HeroMockPage lockedVariant="female" />
      </Suspense>
    );
  }
  // Default to A — current live experience (SaaSLandingPage already renders
  // its own SecurityNoticeBanner above the hero, so don't double it here).
  return <SaaSLandingPage />;
}
